import React, {
    useContext,
    useEffect,
    useState,
    useRef,
    useCallback,
} from "react";
import { useAuth } from "hooks/Auth";
import { toast } from "react-hot-toast";
import { apiGet, apiPost } from "helpers/Request";
import { AssignmentContext } from "context/Assignment";
import { transform as assignmentCommentTransformer } from "schemas/AssignmentComment";
import { useAsyncError } from "hooks/AsyncError";
import Label from "components/FormElements/Label";
import RichEditor from "components/FormElements/RichEditor";
import { Formik, Form } from "formik";
import PrimaryButton from "components/Buttons/PrimaryButton";
import striptags from "striptags";
import { fromSqlDateTime, SQL_DATE_TIME_FORMAT } from "helpers/Date";
import { packClasses } from "Utils";

export default function AssignmentComments(props) {
    const auth = useAuth();
    const throwError = useAsyncError();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const formRef = useRef();

    const { assignment } = useContext(AssignmentContext);

    const onSubmit = (values) => {
        setSaving(true);
        toast.success("Saving...");
        apiPost("/v4/assignment-comment/" + assignment.uuid, auth.publication, {
            comment: values.comment,
        })
            .then((response) => {
                setComments(assignmentCommentTransformer(response));
                toast.success("Comment saved");
            })
            .catch((err) => {
                throwError(new Error("Unable to save comment"));
                console.error(err);
            })
            .finally(() => {
                setSaving(false);
            });
    };

    const load = useCallback(() => {
        setLoading(true);
        apiGet("/v4/assignment-comment/" + assignment.uuid, auth.publication)
            .then((resp) => {
                setComments(assignmentCommentTransformer(resp));
            })
            .catch((err) => {
                throwError(new Error("Unable to load comments."));
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [auth.publication, assignment, throwError]);

    useEffect(() => {
        if (!assignment || !assignment.uuid) {
            return;
        }
        load();
    }, [assignment, load]);

    if (!assignment || !assignment.uuid) {
        return null;
    }

    if (loading) {
        return <div>Loading comments</div>;
    }
    return (
        <div className="w-full md:w-3/4">
            <hr className={packClasses("my-4", !comments.length && "hidden")} />
            <div className="space-y-4">
                {comments.map((comment) => (
                    <div
                        key={comment.uuid}
                        className="rounded-md bg-white p-4 shadow-sm"
                    >
                        <div
                            className="prose"
                            dangerouslySetInnerHTML={{
                                __html: striptags(comment.comment, [
                                    "a",
                                    "p",
                                    "br",
                                    "ul",
                                    "li",
                                    "ol",
                                    "div",
                                ]),
                            }}
                        />
                        <div className="mt-4 text-xs font-light">
                            {comment.user.name} at{" "}
                            {fromSqlDateTime(comment.created_at).toFormat(
                                SQL_DATE_TIME_FORMAT
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <hr className="my-4" />
            <Formik
                innerRef={formRef}
                initialValues={{ comment: "" }}
                onSubmit={onSubmit}
                enableReinitialize={false}
            >
                {({ setFieldValue }) => (
                    <Form className="space-y-2">
                        <div>
                            <Label>New Comment</Label>
                            <RichEditor name="comment" />
                        </div>
                        <PrimaryButton type="submit" disabled={saving}>
                            Save Comment
                        </PrimaryButton>
                    </Form>
                )}
            </Formik>
        </div>
    );
}
